import { FC } from 'react';
import styled from 'styled-components';
import useTheme from '../../utils/useTheme';
import { Alert } from '../Alert/legacy/Alert';
import { Icon } from '../Icon/Icon';
import { LabelTooltip } from '../Label/LabelTooltip';
import { Grid } from '../Grid/Grid';
import { LinkButton } from '../LinkButton/LinkButton';
import { Spinner } from '../Spinner/Spinner';
import { Position, Tooltip } from '..';
import { IconElement } from '../SearchMenu/SearchMenuItem.styles';
import {
  DashboardPanel,
  HeadingElement,
  HeadingTooltipWrapper,
  SpinnerContainer,
  ErrorOverlay,
  ErrorElement,
  ErrorAction,
  ContentElement,
} from '../TimeSeriesHighPanel/TimeSeriesHighPanel';
import { Text } from '../';

const StatsWrapper = styled.div`
  padding-top: 30px;
`;

export interface IProps {
  /** Heading text that appears above the chart. */
  heading?: string;
  /** Optional tooltip heading.  Defaults to undefined. */
  headingTooltipTitle?: string;
  /** Optional tooltip description.  Defaults to undefined. */
  headingTooltipDescription?: string;
  /** Optional tooltip content.  Defaults to undefined. */
  headingTooltipContent?: JSX.Element;
  /** Optional tooltip width. Defaults to auto */
  headingTooltipWidth?: string;
  /** Optional tooltip position. Defaults to right. */
  headingTooltipPosition?: Position;
  /** Show the spinner alongside the heading. */
  showHeadingSpinner?: boolean;
  /** Icon to show alongside the heading. */
  icon?: string;
  /** Show error message. */
  isError?: boolean;
  /** Error message to display. (Default: 'Error loading graph data.')*/
  errorMessage?: string;
  /** Handle error action click. */
  handleErrorActionClick?: () => void;
  /** Error action message. (Default: 'Please refresh.')*/
  errorActionMessage?: string;
}

export const StatsPanel: FC<IProps> = ({
  children,
  heading,
  headingTooltipContent,
  headingTooltipDescription,
  headingTooltipPosition,
  headingTooltipTitle,
  headingTooltipWidth,
  icon,
  showHeadingSpinner,
  isError,
  errorMessage,
  handleErrorActionClick,
  errorActionMessage,
}) => {
  const theme = useTheme();

  return (
    <DashboardPanel>
      <ContentElement isError={isError}>
        {heading && (
          <HeadingElement>
            {icon && (
              <IconElement>
                <Icon icon={icon} />
              </IconElement>
            )}

            <Text size={18} weight="bold">
              {heading}
            </Text>

            {(headingTooltipDescription || headingTooltipContent) && (
              <HeadingTooltipWrapper>
                <Tooltip
                  position={headingTooltipPosition}
                  html={
                    <LabelTooltip
                      heading={headingTooltipTitle}
                      description={headingTooltipDescription}
                      content={headingTooltipContent}
                      width={headingTooltipWidth}
                    />
                  }
                >
                  <Icon icon="question-circle" size="small" color={theme.vars.newColor.darkGS2} />
                </Tooltip>
              </HeadingTooltipWrapper>
            )}

            {showHeadingSpinner && (
              <SpinnerContainer>
                <Spinner size="small" />
              </SpinnerContainer>
            )}
          </HeadingElement>
        )}

        <StatsWrapper>
          <Grid isMaximised={true} gridGap={40}>
            {children}
          </Grid>
        </StatsWrapper>
      </ContentElement>

      {isError && (
        <>
          <ErrorOverlay />

          <ErrorElement>
            <Alert
              variation="error"
              heading={errorMessage}
              headingElement={
                handleErrorActionClick &&
                errorActionMessage && (
                  <ErrorAction>
                    <LinkButton onClick={handleErrorActionClick}>{errorActionMessage}</LinkButton>
                  </ErrorAction>
                )
              }
            />
          </ErrorElement>
        </>
      )}
    </DashboardPanel>
  );
};

export default StatsPanel;
